import { useEffect, useState } from 'react';
import {
  CalculateFinancialSummaryRequest,
  FinancialSummary,
} from '../../../../../clients/quipli-service/financials/financials';
import {
  CanOffRentValidationError,
  OffRentRequest,
  Order,
  OrderType,
} from '../../../../../clients/quipli-service/order/order';
import { Dictionary } from 'lodash';
import { getUpdatedItemRequestsFromFuelCharges, shouldUpdateItemEndDate } from '../../shared/utils/off-rent-utils';
import { FuelCharge } from '../../shared/types/fuel-charge-types';
import { useAPI } from '../../../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../../../util/toasts';
import { safeRound } from '../../../../../util/util';
import { useOrderApiV1 } from '../../../../../clients/quipli-service/order/order-api-v1';
import { RentalProductPrice } from '../../../../../clients/quipli-service/pricing/pricing';
import { Configuration } from '../../../../../clients/quipli-service/configuration/configuration';

export const useOffRentFinancials = (
  order: Order | undefined,
  offRentRequest: OffRentRequest | undefined,
  fuelCharges: FuelCharge[],
  showSelectProductsStep: boolean,
  customPrices: RentalProductPrice[] | undefined,
  configs: Dictionary<Configuration> | undefined,
  onOffRentUpdateDateCheckbox: boolean,
  onOffRentUpdateDateConfiguration: boolean,
  setCanOffRent: (canOffRent: CanOffRentValidationError[]) => void,
  existingSummaryRequest: CalculateFinancialSummaryRequest | undefined,
  setExistingSummaryRequest: React.Dispatch<React.SetStateAction<CalculateFinancialSummaryRequest | undefined>>,
) => {
  const { calculateFinancialSummary } = useAPI();
  const { canOffRentOrder } = useOrderApiV1();

  const [existingSummary, setExistingSummary] = useState<FinancialSummary>();
  const [lateChargeSummaryRequest, setLateChargeSummaryRequest] = useState<CalculateFinancialSummaryRequest>();
  const [lateChargeSummary, setLateChargeSummary] = useState<FinancialSummary>();
  const [isFinancialSummaryLoading, setIsFinancialSummaryLoading] = useState<boolean>(false);

  useEffect(() => {
    setExistingSummaryRequest((prev) => {
      if (!offRentRequest) {
        return prev;
      }

      const updatedItemRequests = getUpdatedItemRequestsFromFuelCharges(fuelCharges, offRentRequest, prev);

      if (prev) {
        return {
          ...prev,
          items: updatedItemRequests,
        };
      }
      return prev;
    });
  }, [fuelCharges, offRentRequest]);

  // This effect is used to recalculate the late charge summary when the request changes
  useEffect(() => {
    const calculateLateChargeSummary = async (request: CalculateFinancialSummaryRequest) => {
      try {
        const fs = await calculateFinancialSummary(request);
        setLateChargeSummary(fs);
      } catch (e) {
        console.log(e);
        toast('Error calculating financial summary', 'error');
      } finally {
        setIsFinancialSummaryLoading(false);
      }
    };

    if (lateChargeSummaryRequest) {
      setIsFinancialSummaryLoading(true);

      calculateLateChargeSummary(lateChargeSummaryRequest);
    }
  }, [lateChargeSummaryRequest]);

  // This effect is used to recalculate the financial summary when the existing summary request changes
  useEffect(() => {
    if (existingSummaryRequest && order) {
      setIsFinancialSummaryLoading(true);
      calculateFinancialSummary(existingSummaryRequest)
        .then((fs) => {
          const customPriceProducts = order.products.filter((op) =>
            fs.lineItems.some(
              (li) => li.type === 'Product' && li.id === `${op.id}` && li.unitPrice.toUnit() !== op.customerBuyingPrice,
            ),
          );
          if (customPriceProducts.length > 0) {
            const requestWithCustomPrices = {
              ...existingSummaryRequest,
              items: existingSummaryRequest.items.map((i) => {
                const op = customPriceProducts.find((op) => i.id === `${op.id}`);
                if (op) {
                  return { ...i, customPrice: safeRound(op.customerBuyingPrice * 100) };
                } else {
                  return i;
                }
              }),
            };
            setExistingSummaryRequest(requestWithCustomPrices);
          } else {
            setExistingSummary(fs);
          }
        })
        .finally(() => setIsFinancialSummaryLoading(false));
    }
  }, [existingSummaryRequest, order]);

  // This effect is used to recalculate the late charge summary when the checkbox is toggled
  useEffect(() => {
    setLateChargeSummaryRequest((prev) => {
      if (prev) {
        // If unchecked, set the end date to the rental end date so summary recalculates
        if (!onOffRentUpdateDateCheckbox) {
          const newItems = prev.items.map((item) => {
            if (item.type === OrderType.Rental) {
              return {
                ...item,
                end: order?.products.find((op) => op.id === parseInt(item.id))?.rentalEndDate,
              };
            }
            return item;
          });
          return {
            ...prev,
            items: newItems,
          };
        } else {
          return {
            ...prev,
            items: prev.items.map((item) => {
              if (item.type === OrderType.Rental) {
                return {
                  ...item,
                  end: offRentRequest?.timestamp,
                };
              }
              return item;
            }),
          };
        }
      }
    });
  }, [onOffRentUpdateDateCheckbox]);

  useEffect(() => {
    if (
      !showSelectProductsStep &&
      order &&
      offRentRequest &&
      ((!order.isCycleBilled && customPrices) || fuelCharges) &&
      existingSummaryRequest
    ) {
      canOffRentOrder(order.id, offRentRequest)
        .then(setCanOffRent)
        .catch((e) => toast(e.message, 'error'));

      const lateRequest = {
        ...existingSummaryRequest,
        items: [
          ...existingSummaryRequest.items.map((item) => {
            if (
              shouldUpdateItemEndDate(
                item,
                offRentRequest,
                configs,
                order,
                onOffRentUpdateDateCheckbox,
                onOffRentUpdateDateConfiguration,
              )
            ) {
              return {
                ...item,
                end: offRentRequest.timestamp,
              };
            }
            return item;
          }),
        ],
      };
      setLateChargeSummaryRequest(lateRequest);
    }
  }, [
    order,
    offRentRequest,
    customPrices,
    existingSummaryRequest,
    showSelectProductsStep,
    configs,
    onOffRentUpdateDateConfiguration,
    onOffRentUpdateDateCheckbox,
  ]);

  return {
    existingSummaryRequest,
    setExistingSummaryRequest,
    existingSummary,
    setExistingSummary,
    lateChargeSummaryRequest,
    setLateChargeSummaryRequest,
    lateChargeSummary,
    setLateChargeSummary,
    isFinancialSummaryLoading,
    setIsFinancialSummaryLoading,
  };
};
